import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

// Define Listable type
type Listable = {
  slug: string;
  name: string;
  webpPath?: string;
  imageWidth?: number;
  imageHeight?: number;
};

// Define Props type
type Props = {
  pathPrefix: string;
  listables: Array<Listable>;
};

const ITEMS_PER_PAGE = 16; // Set the number of items per page

export default function PaginatedListableList({
  pathPrefix,
  listables,
}: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Filter listables based on search term
  const filteredListables = listables.filter((listable) =>
    listable.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageCount = Math.ceil(filteredListables.length / ITEMS_PER_PAGE);
  const firstItemIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const lastItemIndex = firstItemIndex + ITEMS_PER_PAGE;
  const currentListables = filteredListables.slice(
    firstItemIndex,
    lastItemIndex
  );

  // Define a type for pagination items
  type PaginationItem = {
    value: number | string;
  };

  // Function to calculate the range of numbers to display
  const paginationRange = (): PaginationItem[] => {
    const siblingCount = 1;
    const range: PaginationItem[] = [];

    for (
      let i = Math.max(2, currentPage - siblingCount);
      i <= Math.min(pageCount - 1, currentPage + siblingCount);
      i++
    ) {
      range.push({ value: i });
    }

    if (!range.some((p) => p.value === 1)) {
      range.unshift({ value: 1 });
      if (
        range.length > 1 &&
        typeof range[1].value === 'number' &&
        (range[1].value as number) > 2
      ) {
        range.splice(1, 0, { value: '...' });
      }
    }

    if (!range.some((p) => p.value === pageCount)) {
      if (
        range.length > 0 &&
        typeof range[range.length - 1].value === 'number' &&
        (range[range.length - 1].value as number) < pageCount - 1
      ) {
        range.splice(range.length, 0, { value: '...' });
      }
      range.push({ value: pageCount });
    }

    return range;
  };

  return (
    <>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded-md py-1 px-2 w-full md:w-1/2"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 not-prose">
        {currentListables.map((listable) => (
          <div
            key={listable.slug}
            className="relative flex items-center px-6 py-5 space-x-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 h-24"
          >
            <div className="flex-1 min-w-0">
              <Link
                href={`${pathPrefix}/${listable.slug}`}
                className="focus:outline-none"
              >
                <span className="absolute inset-0" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">
                  {listable.name}
                </p>
              </Link>
            </div>
            <div className="flex-shrink-0">
              {listable.webpPath &&
                listable.imageWidth &&
                listable.imageHeight && (
                  <div className="w-20">
                    <Image
                      src={listable.webpPath}
                      width={70 * (listable.imageWidth / listable.imageHeight)}
                      height={70}
                      alt={listable.name}
                      loading="lazy"
                    />
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-center mt-4 items-center">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          className={`mx-1 px-4 py-2 rounded-md ${
            currentPage === 1
              ? 'opacity-50 cursor-not-allowed'
              : 'bg-white border'
          }`}
        >
          Previous
        </button>
        {paginationRange().map((item, index) => (
          <button
            key={index}
            onClick={() =>
              typeof item.value === 'number' &&
              setCurrentPage(item.value as number)
            }
            className={`mx-1 px-4 py-2 rounded-md ${
              currentPage === item.value
                ? 'bg-blue-500 text-white'
                : 'bg-white border'
            }`}
            disabled={typeof item.value === 'string'}
          >
            {item.value}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage(Math.min(pageCount, currentPage + 1))}
          className={`mx-1 px-4 py-2 rounded-md ${
            currentPage === pageCount
              ? 'opacity-50 cursor-not-allowed'
              : 'bg-white border'
          }`}
        >
          Next
        </button>
      </div>
    </>
  );
}
